import React, { useState, useEffect } from "react";
import { Route, BrowserRouter as Router, Routes, Navigate } from "react-router-dom";
import PatientDetailsPage from "../PatientDetailsPage/PatientDetailPage";
import PatientListPage from "../PatientListPage/PatientListPage";
import LoginPage from "../LoginPage/LoginPage";
import CognitoLoginPage from "../LoginPage/CognitoLoginPage";
import CognitoLogoutPage from "../LoginPage/CognitoLogoutPage";
import ForgotPasswordPage from "../ForgotPasswordPage/ForgotPasswordPage";
import ChangePasswordPage from "../ChangePasswordPage/ChangePasswordPage";
import ChangeEmailPage from "../ChangeEmailPage/ChangeEmailPage";
import NotificationSettingsPage from "../NotificationSettingsPage/NotificationSettingsPage";
import ProtectedRoute, { PrivateRoute, SwitchingRoute } from "../ProtectedRoute";
import AppContext from "../../context/AppContext";
import { useMediaQuery } from "@mui/material";
import theme from "../theme";
import PatientReportsCondense from "../PatientReportsCondense/PatientReportsCondense";
import { useApolloClient } from "@apollo/client/react";
import authService, { SET_AUTH } from "../../services/authService";
import CumulativeContextProvider from "../../context/CumulativeContext";
import PatientDetailsContextProvider from "../../context/PatientDetailsContext";
import CumulativeMobilePage from "../CumulativeMobilePage/CumulativeMobilePage";
import PrintableReportsPage from "../PrintableReports/PrintableReportsPage";
import FoodPrintReport from "../PrintableReports/FoodPrintReport";
import CheckEmailPage from "../ForgotPasswordPage/CheckEmailPage";
import UserGuidePage from "../UserGuide/UserGuidePage";
import ResetPasswordPage from "../ForgotPasswordPage/ResetPasswordPage";
import ContactSupportPage from "../ForgotPasswordPage/ContactSupportPage";
import LinkExpiredPage from "../ForgotPasswordPage/LinkExpiredPage";
import PrintableCombineAtomicPage from "../PrintableReports/PrintableCombineAtomicPage";
import PrintableCombineCtAtomicPage from "../PrintableReports/PrintableCombineCtAtomicPage";
import HospAdminUserListPage from "../HospAdminUserListPage/HospAdminUserListPage";
import Page from "./page";
import HistoImagePage from "../HistoImagePage/HistoImagePage";
import MFAEnrolmentPage from "../MFAEnrolment/MFAEnrolmentPage";
import MFAAuthenticationPage from "../MFAAuthentication/MFAAuthenticationPage";
import PreviousTestRequests from "../PreviousTestRequests/PreviousTestRequests";
import StudyPage from "../study/StudyPage";
import EpisodeListPage from "../PatientEpisodesPage/EpisodeListPage";
import PrintablePatientsPage from "../PrintableReports/PrintablePatientsPage";
import { PatientsContextProvider } from "../../context/PatientsContext";
import PrintablePatientsDelegationPage from "../PrintableReports/PrintablePatientsDelegationsPage";
import CPDprogramsPage from "../CPDprograms/CPDprogramsPage";
import CPDcytologyPage from "../CPDprograms/CPDcytologyPage";
import CPDdiabetePages from "../CPDprograms/CPDdiabetesPage";
import CPDskinPage from "../CPDprograms/CPDskinPage";
import CPDcytologyDetailPage from "../CPDprograms/CPDcytologyDetailPage";
import DeleteAccountPage from "../DeleteAccountPage/DeleteAccountPage";
import HospitalReportsPage from "../HospitalReports/HospitalReportsPage";
import AntiBiogramReportPage from "../HospitalReports/AntiBiogram/AntiBiogramReportPage";
import SignalResistanceReportPage from "../HospitalReports/AntiBiogram/SignalResistanceReportPage";
import { pdfjs } from "react-pdf";
import UnsubscribeDiabetesPage from "../UnsubscribeDiabetesPage/UnsubscribeDiabetesPage";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.wrapper.mjs";

const App: React.FC = () => {
  const mainTitle = `eResults`;
  const isDesktop: boolean = useMediaQuery(theme.breakpoints.up("lg"));
  const [error, setError] = useState("");
  const [testName, setTestName] = useState("");
  const [prevError, setPrevError] = useState("");
  const [isAuth, setIsAuth] = useState(authService.isAuthenticated());
  const [isCognito] = useState(authService.isCognito());
  const [errorAcknowledged, setErrorAcknowledged] = useState(false);
  const client = useApolloClient();
  const [refreshResult, setRefreshResult] = useState(false);
  const [autoRefreshResult, setAutoRefreshResult] = useState(
    localStorage.getItem("AutoRefreshResult") === "true" ? true : false,
  );
  const [previewMode, setPreviewMode] = useState(localStorage.getItem("PreviewMode") === "true" ? true : false);
  const [success, setSuccess] = useState("");
  const [prevSuccess, setPrevSuccess] = useState("");
  const [successAcknowledged, setSuccessAcknowledged] = useState(false);

  useEffect(() => {
    if (error !== prevError) {
      setErrorAcknowledged(false);
      setPrevError(error);
    }
  }, [error, prevError, setPrevError, errorAcknowledged]);

  useEffect(() => {
    if (success !== prevSuccess) {
      setSuccessAcknowledged(false);
      setPrevSuccess(success);
    }
  }, [success, prevSuccess, setPrevSuccess, successAcknowledged]);

  client.cache.watch({
    query: SET_AUTH,
    optimistic: false,
    callback: ({ complete, result }) => {
      if (complete && result.auth.isAuth) {
        setIsAuth(true);
      } else if (complete && !result.auth.isAuth) {
        setIsAuth(false);
      }
    },
  });

  return (
    <AppContext.Provider
      value={{
        isDesktop,
        error,
        setError,
        errorAcknowledged,
        setErrorAcknowledged,
        testName,
        setTestName,
        refreshResult,
        setRefreshResult,
        autoRefreshResult,
        setAutoRefreshResult,
        previewMode,
        setPreviewMode,
        success,
        setSuccess,
        successAcknowledged,
        setSuccessAcknowledged,
      }}
    >
      <Router>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route
            path="/login"
            element={
              <Page title={mainTitle}>
                <LoginPage />
              </Page>
            }
          />

          <Route
            path="/login-sso"
            element={
              <Page title={mainTitle}>
                <CognitoLoginPage />
              </Page>
            }
          />

          <Route path="/logout-sso" element={<CognitoLogoutPage />} />
          <Route path="/forgot" element={<ForgotPasswordPage />} />
          <Route path="/check-email/:email" element={<CheckEmailPage />} />
          <Route path="/userguide" element={<UserGuidePage />} />
          <Route path="/reset/:token" element={<ResetPasswordPage />} />
          <Route path="/contact-support" element={<SwitchingRoute component={ContactSupportPage} />} />
          <Route path="/expired" element={<LinkExpiredPage />} />
          <Route path="/patient-opt-out" element={<UnsubscribeDiabetesPage />} />

          <Route
            path="/change-password"
            element={
              <ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={ChangePasswordPage} title={mainTitle} />
            }
          />

          <Route
            path="/patient/:id/:reportid/histo-image"
            element={
              <ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={HistoImagePage} title={mainTitle} />
            }
          />

          <Route
            path="/account-settings"
            element={
              <ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={ChangeEmailPage} title={mainTitle} />
            }
          />

          <Route
            path="/previous-test-requests"
            element={
              <ProtectedRoute
                isAuth={isAuth}
                isCognito={isCognito}
                component={PreviousTestRequests}
                title={mainTitle}
              />
            }
          />

          <Route
            path="/notification-settings"
            element={
              <ProtectedRoute
                isAuth={isAuth}
                isCognito={isCognito}
                component={NotificationSettingsPage}
                title={mainTitle}
              />
            }
          />

          <Route
            path="/delete-account"
            element={
              <ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={DeleteAccountPage} title={mainTitle} />
            }
          />

          <Route
            path="/enrolment"
            element={
              <ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={MFAEnrolmentPage} title={mainTitle} />
            }
          />

          <Route
            path="/mfaauthentication"
            element={
              <ProtectedRoute
                isAuth={isAuth}
                isCognito={isCognito}
                component={MFAAuthenticationPage}
                title={mainTitle}
              />
            }
          />

          <Route
            path="/patients"
            element={
              <ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={PatientListPage} title={mainTitle} />
            }
          />

          <Route
            path="/study"
            element={<ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={StudyPage} title={mainTitle} />}
          />

          <Route
            path="/groupadmin"
            element={
              <ProtectedRoute
                isAuth={isAuth}
                isCognito={isCognito}
                component={HospAdminUserListPage}
                title={mainTitle}
              />
            }
          />
          <Route
            path="/cpd-skin"
            element={<ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={CPDskinPage} title={mainTitle} />}
          />
          <Route
            path="/cpd-diabetes/:tabpage?"
            element={
              <ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={CPDdiabetePages} title={mainTitle} />
            }
          />
          <Route
            path="/cpd-cytology"
            element={
              <ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={CPDcytologyPage} title={mainTitle} />
            }
          />
          <Route
            path="/cpd-cytology-report/:id"
            element={
              <ProtectedRoute
                isAuth={isAuth}
                isCognito={isCognito}
                component={CPDcytologyDetailPage}
                title={mainTitle}
              />
            }
          />
          <Route
            path="/cpd-programs"
            element={
              <ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={CPDprogramsPage} title={mainTitle} />
            }
          />
          <Route
            path="/hospital-reports"
            element={
              <ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={HospitalReportsPage} title={mainTitle} />
            }
          />
          <Route
            path="/hospital-reports/antibiogram-report/:filename"
            element={
              <ProtectedRoute
                isAuth={isAuth}
                isCognito={isCognito}
                component={AntiBiogramReportPage}
                title={mainTitle}
              />
            }
          />
          <Route
            path="/hospital-reports/antibiogram/signal-resistance/:filename"
            element={
              <ProtectedRoute
                isAuth={isAuth}
                isCognito={isCognito}
                component={SignalResistanceReportPage}
                title={mainTitle}
              />
            }
          />
          <Route
            path="/patient-print/:id/:allPatients/labnumber"
            element={
              <PrivateRoute isAuth={isAuth} isCognito={isCognito}>
                <PrintableReportsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/combine-atomic-print/patient/:patientId/labnumber"
            element={
              <PrivateRoute isAuth={isAuth} isCognito={isCognito}>
                <PrintableCombineAtomicPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/combine-atomic-print/patients"
            element={
              <PrivateRoute isAuth={isAuth} isCognito={isCognito}>
                <PrintablePatientsPage />
              </PrivateRoute>
            }
          />

          <Route
            path="/combine-atomic-delegate-print/patients"
            element={
              <PrivateRoute isAuth={isAuth} isCognito={isCognito}>
                <PrintablePatientsDelegationPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/patient/foodprint"
            element={
              <PrivateRoute isAuth={isAuth} isCognito={isCognito}>
                <FoodPrintReport />
              </PrivateRoute>
            }
          />

          <Route
            path="/patient-episodes"
            element={
              <PrivateRoute isAuth={isAuth} isCognito={isCognito} title={mainTitle}>
                <PatientsContextProvider>
                  <ProtectedRoute isAuth={isAuth} isCognito={isCognito} component={EpisodeListPage} title={mainTitle} />
                </PatientsContextProvider>
              </PrivateRoute>
            }
          />

          <Route
            path="/ct-combine-atomic-print/patient/:patientId/labnumber"
            element={
              <PrivateRoute isAuth={isAuth} isCognito={isCognito}>
                <PatientDetailsContextProvider>
                  <CumulativeContextProvider>
                    <PrintableCombineCtAtomicPage />
                  </CumulativeContextProvider>
                </PatientDetailsContextProvider>
              </PrivateRoute>
            }
          />

          {/* 
            AS OF V7, We scrap the regex control from here and will be checked in respective components
          */}
          <Route
            path="/patient/:id/:allPatients/labnumber/:sortBy?"
            element={
              <PrivateRoute isAuth={isAuth} isCognito={isCognito}>
                <PatientDetailsContextProvider>
                  <CumulativeContextProvider>
                    <PatientDetailsPage />
                  </CumulativeContextProvider>
                </PatientDetailsContextProvider>
              </PrivateRoute>
            }
          />
          <Route
            path="/report/:id/:allPatients/labnumber/:sortBy?"
            element={
              <PrivateRoute isAuth={isAuth} isCognito={isCognito}>
                <PatientDetailsContextProvider>
                  <CumulativeContextProvider>
                    <PatientReportsCondense />
                  </CumulativeContextProvider>
                </PatientDetailsContextProvider>
              </PrivateRoute>
            }
          />

          <Route
            path="/atomic/:patientId/:allPatients/:panelId"
            element={
              <PrivateRoute isAuth={isAuth} isCognito={isCognito}>
                <PatientDetailsContextProvider>
                  <CumulativeContextProvider>
                    <CumulativeMobilePage />
                  </CumulativeContextProvider>
                </PatientDetailsContextProvider>
              </PrivateRoute>
            }
          />

          <Route path="/version" element={<div>Version: {import.meta.env.VITE_VERSION}</div>} />
          <Route path="/not-found" element={<div>404 NOT FOUND</div>} />
          <Route path="*" element={<div>404 NOT FOUND</div>} />
        </Routes>
      </Router>
    </AppContext.Provider>
  );
};

export default App;
